import type { DocumentGroup } from "services/document-group-api";
import type { MetadocMetadata } from "services/metadoc-api";
import type { TabId } from "../metadoc.types";

export type CodeDateDetailValue = {
  date?: null | string;
  description?: null | string;
  model_description?: null | string;
  notes?: null | string;
  page_number?: number | null;
};

export type CodeMetadataValue = {
  code?: null | string;
  code_type?: null | string;
  section?: null | string;
  facility?: null | string;
  condition_type?: null | string;
  date_details?: CodeDateDetailValue[];
};

export type EditableCodeDateDetail = {
  id: string;
  date: string;
  description: string;
  notes: string;
  reference: string;
  pageNumber: string;
};

export type NonEmptyArray<T> = [T, ...T[]];

export type CodeStatus = "accepted" | "denied" | "unrelated";
export type SupportedCodeType = "ICD-9" | "ICD-10" | "CPT" | "HCPCS" | "DRG";

export type CodeSection =
  | "source"
  | "diagnosticsadvancedimaging"
  | "surgerywithdate"
  | "injectionsprocedures"
  | "dmewithdate"
  | "not accepted or denied"
  | "other";

export const CODE_SECTION_LABELS: Record<CodeSection, string> = {
  source: "Diagnosis",
  diagnosticsadvancedimaging: "Diagnostics / Advanced Imaging",
  surgerywithdate: "Surgery",
  injectionsprocedures: "Injections / Procedures",
  dmewithdate: "Durable Medical Equipment",
  "not accepted or denied": "Not Accepted or Denied",
  other: "Other",
};

export const getSectionLabel = (section: string | null | undefined): string => {
  if (!section) return "";
  const lower = section.toLowerCase() as CodeSection;
  return CODE_SECTION_LABELS[lower] ?? section;
};

const normalizeSection = (section: string | null | undefined): CodeSection => {
  if (!section) return "other";
  const lower = section.toLowerCase();
  if (lower in CODE_SECTION_LABELS) return lower as CodeSection;
  return "other";
};
export type AddCodePreset = {
  codeType: SupportedCodeType;
  code: string;
  description: string;
};

export type EditableCodeValue = {
  id: number;
  code: string;
  codeType: SupportedCodeType | "";
  conditionType: CodeStatus;
  section: CodeSection;
  documentGroupId: number | null;
  provider: string;
  facility: string;
  dateDetails: NonEmptyArray<EditableCodeDateDetail>;
};

export type MedicationField =
  | "date"
  | "providerName"
  | "name"
  | "dosage"
  | "frequency";

export type EditableMedicationValue = {
  id: number;
  documentGroupId: number | null;
  date: string;
  providerName: string;
  name: string;
  dosage: string;
  frequency: string;
};

export type MedicationMetadataValue = {
  name?: null | string;
  dosage?: null | string;
  frequency?: null | string;
};

const supportedCodeTypes = new Set<SupportedCodeType>([
  "ICD-9",
  "ICD-10",
  "CPT",
  "HCPCS",
  "DRG",
]);
const validCodeStatuses = [
  "accepted",
  "denied",
  "unrelated",
] as const satisfies readonly CodeStatus[];

const getCodeType = (codeType?: null | string): SupportedCodeType | "" => {
  if (!codeType) {
    return "";
  }

  const normalized = codeType.trim().replace(/_/g, "-").toUpperCase();
  if (!normalized) {
    return "";
  }

  if (normalized === "ICD9" || normalized === "ICD-9") {
    return "ICD-9";
  }

  if (normalized === "ICD10" || normalized === "ICD-10") {
    return "ICD-10";
  }

  if (supportedCodeTypes.has(normalized as SupportedCodeType)) {
    return normalized as SupportedCodeType;
  }

  return "";
};

type MedicationListMetadataValue = MedicationMetadataValue;

type TiptapTextNode = {
  type: "text";
  text: string;
};

type TiptapParagraphNode = {
  type: "paragraph";
  content?: TiptapTextNode[];
};

type TiptapListItemNode = {
  type: "listItem";
  content: TiptapParagraphNode[];
};

type TiptapBulletListNode = {
  type: "bulletList";
  content: TiptapListItemNode[];
};

type TiptapNode = TiptapParagraphNode | TiptapBulletListNode;

type TiptapDoc = {
  type: "doc";
  content: TiptapNode[];
};

/**
 * normalizes raw metadata values into a plain object.
 * accepts either a json string from the api or an object already parsed upstream.
 */
export const parseMetadataValue = (
  value: unknown,
): Record<string, unknown> | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
};

const isSerializedJSONObjectString = (value: string): boolean => {
  const normalized = value.trim();
  return normalized.startsWith("{") && normalized.endsWith("}");
};

/**
 * creates a tiptap paragraph node from a single text value.
 * empty text becomes an empty paragraph so the document shape stays valid.
 */
const createParagraphNode = (text: string): TiptapParagraphNode => ({
  type: "paragraph",
  content: text ? [{ type: "text", text }] : undefined,
});

/**
 * converts a list of strings into a tiptap bullet list.
 * each entry becomes one list item with a paragraph child.
 */
const createBulletListNode = (items: string[]): TiptapBulletListNode => ({
  type: "bulletList",
  content: items.map((item) => ({
    type: "listItem",
    content: [createParagraphNode(item)],
  })),
});

const toTipTapJSON = (content: TiptapNode[]): string =>
  JSON.stringify({
    type: "doc",
    content,
  } satisfies TiptapDoc);

const getDateDetails = (value: unknown): CodeDateDetailValue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is CodeDateDetailValue => {
    return typeof item === "object" && item !== null && !Array.isArray(item);
  });
};

const isOptionalNullableString = (
  value: unknown,
): value is null | string | undefined =>
  value === undefined || value === null || typeof value === "string";

const isOptionalNumberOrNull = (
  value: unknown,
): value is number | null | undefined =>
  value === undefined || value === null || typeof value === "number";

const isCodeDateDetailValue = (
  value: unknown,
): value is CodeDateDetailValue => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const detail = value as Record<string, unknown>;
  return (
    isOptionalNullableString(detail.date) &&
    isOptionalNullableString(detail.description) &&
    isOptionalNullableString(detail.model_description) &&
    isOptionalNullableString(detail.notes) &&
    isOptionalNumberOrNull(detail.page_number)
  );
};

const isCodeMetadataValue = (value: unknown): value is CodeMetadataValue => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const metadata = value as Record<string, unknown>;
  return (
    isOptionalNullableString(metadata.code) &&
    isOptionalNullableString(metadata.code_type) &&
    isOptionalNullableString(metadata.section) &&
    isOptionalNullableString(metadata.facility) &&
    isOptionalNullableString(metadata.condition_type) &&
    (metadata.date_details === undefined ||
      (Array.isArray(metadata.date_details) &&
        metadata.date_details.every(isCodeDateDetailValue)))
  );
};

const isMedicationListMetadataValue = (
  value: unknown,
): value is MedicationListMetadataValue => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const metadata = value as Record<string, unknown>;
  const hasOnlySupportedFields = Object.keys(metadata).every((key) =>
    ["name", "dosage", "frequency", "type"].includes(key),
  );

  if (!hasOnlySupportedFields) {
    return false;
  }

  return (
    isOptionalNullableString(metadata.name) &&
    isOptionalNullableString(metadata.dosage) &&
    isOptionalNullableString(metadata.frequency) &&
    (typeof metadata.name === "string" ||
      typeof metadata.dosage === "string" ||
      typeof metadata.frequency === "string")
  );
};

type MedicationMetadataRow = MetadocMetadata & {
  date?: null | string;
  provider_name?: null | string;
};

type CodeMetadataRow = MetadocMetadata & {
  provider_name?: null | string;
};

const isValidCodeStatus = (value: string): value is CodeStatus =>
  validCodeStatuses.some((status) => status === value);

/**
 * maps incoming condition labels onto the supported editor statuses.
 * missing or unknown values fall back to `unrelated`.
 */
export const getConditionType = (conditionType?: string): CodeStatus => {
  const normalized = conditionType?.toLowerCase();
  return normalized && isValidCodeStatus(normalized) ? normalized : "unrelated";
};

/**
 * transforms api code metadata rows into the editor card model.
 * every returned code is guaranteed to have at least one date detail row.
 */
export const toEditableCodeValues = (
  metadata: MetadocMetadata[],
): EditableCodeValue[] =>
  metadata.map((item) => {
    const codeMetadataRow = item as CodeMetadataRow;
    const parsedValue =
      typeof item.value === "object" && item.value !== null
        ? parseMetadataValue(item.value)
        : null;
    const codeMetadataValue = isCodeMetadataValue(parsedValue)
      ? parsedValue
      : null;
    const defaultReference = "";
    const fallbackDetail: EditableCodeDateDetail = {
      id: `${item.id}-0`,
      date: item.date ?? "",
      description: "",
      notes: "",
      reference: defaultReference,
      pageNumber: "",
    };
    const mappedDetails = getDateDetails(codeMetadataValue?.date_details).map(
      (detail, index) => ({
        id: `${item.id}-${index}`,
        date: detail.date ?? "",
        description: detail.description ?? detail.model_description ?? "",
        notes: detail.notes ?? "",
        reference: defaultReference,
        pageNumber:
          typeof detail.page_number === "number" ? `${detail.page_number}` : "",
      }),
    );
    const dateDetails: NonEmptyArray<EditableCodeDateDetail> =
      mappedDetails.length > 0
        ? [mappedDetails[0], ...mappedDetails.slice(1)]
        : [fallbackDetail];

    return {
      id: item.id,
      code:
        codeMetadataValue?.code ??
        (typeof item.value === "string" &&
        !isSerializedJSONObjectString(item.value)
          ? item.value
          : ""),
      codeType: getCodeType(
        item.code_type ?? codeMetadataValue?.code_type ?? undefined,
      ),
      conditionType: getConditionType(codeMetadataValue?.condition_type),
      section: normalizeSection(codeMetadataValue?.section),
      documentGroupId: item.document_group,
      provider: codeMetadataRow.provider_name ?? "",
      facility: codeMetadataValue?.facility ?? "",
      dateDetails,
    };
  });

/**
 * builds the rich-text medication summary shown in the ai summary editor.
 * only list-style medication rows are included in this aggregated document.
 */
export const formatMedicationSummary = (
  metadata: MetadocMetadata[],
): string => {
  const content: TiptapNode[] = [createParagraphNode("Medications Summary")];
  let hasContent = false;

  for (const item of metadata) {
    const parsedValue = parseMetadataValue(item.value as unknown);
    if (!isMedicationListMetadataValue(parsedValue)) {
      continue;
    }

    const metadataRow = item as MedicationMetadataRow;
    const medicationName = parsedValue.name?.trim() || "Unnamed medication";
    const headerParts = [medicationName];
    const detailItems: string[] = [];

    if (metadataRow.date) {
      headerParts.push(metadataRow.date);
    }

    if (metadataRow.provider_name) {
      headerParts.push(metadataRow.provider_name);
    }

    if (parsedValue.dosage) {
      detailItems.push(`dosage: ${parsedValue.dosage}`);
    }

    if (parsedValue.frequency) {
      detailItems.push(`frequency: ${parsedValue.frequency}`);
    }

    content.push(createParagraphNode(headerParts.join(" - ")));

    if (detailItems.length > 0) {
      content.push(createBulletListNode(detailItems));
    }

    hasContent = true;
  }

  return hasContent ? toTipTapJSON(content) : "";
};

/**
 * converts medication metadata rows into the flat card model used by the panel.
 * malformed rows or rows without medication-list fields are skipped.
 */
export const toEditableMedicationValues = (
  metadata: MetadocMetadata[],
): EditableMedicationValue[] =>
  metadata.flatMap<EditableMedicationValue>((item) => {
    const parsedValue = parseMetadataValue(item.value as unknown);
    if (!isMedicationListMetadataValue(parsedValue)) {
      return [];
    }

    const medicationMetadataRow = item as MedicationMetadataRow;

    return [
      {
        id: item.id,
        documentGroupId: item.document_group,
        date: medicationMetadataRow.date ?? "",
        providerName: medicationMetadataRow.provider_name ?? "",
        name: parsedValue.name ?? "",
        dosage: parsedValue.dosage ?? "",
        frequency: parsedValue.frequency ?? "",
      },
    ];
  });

/**
 * finds the document group that contains a page number from user input.
 * invalid, empty, or out-of-range page values return `null`.
 */
export const getDocumentGroupByPage = (
  documentGroups: DocumentGroup[],
  pageNumber: string,
): DocumentGroup | null => {
  const page = Number(pageNumber);
  if (!Number.isFinite(page) || page <= 0) {
    return null;
  }

  return (
    documentGroups.find(
      (group) => page >= group.start_pg && page <= group.end_pg,
    ) ?? null
  );
};

/**
 * returns the empty-state copy for each ai insights tab.
 * some messages change when a document group is currently selected.
 */
export const getSummaryEmptyMessage = (
  tabId: TabId,
  activeDocumentGroup?: DocumentGroup,
): string => {
  if (tabId === "executive-summary") {
    return "No executive summary is available yet for this metadoc.";
  }

  if (tabId === "per-record") {
    return activeDocumentGroup
      ? `No summary is available yet for this medical record group: ${activeDocumentGroup.document_type}.`
      : "Navigate to a grouped page to view its per-record summary.";
  }

  if (tabId === "codes") {
    return activeDocumentGroup
      ? `No codes are available yet for this medical record group: ${activeDocumentGroup.document_type}.`
      : "Navigate to a grouped page to view its codes.";
  }

  if (tabId === "not-medicare-covered") {
    return "Not Medicare Covered content is not available yet for this metadoc.";
  }

  if (
    tabId === "current-status" ||
    tabId === "current-status-accepted" ||
    tabId === "current-status-denied"
  ) {
    return "No current status is available yet for this metadoc.";
  }

  if (
    tabId === "other-summary" ||
    tabId === "warnings" ||
    tabId === "cost-mitigations"
  ) {
    return activeDocumentGroup
      ? `No warnings or cost mitigations are available yet for this medical record group: ${activeDocumentGroup.document_type}.`
      : "Navigate to a grouped page to view warnings and cost mitigations.";
  }

  return activeDocumentGroup
    ? `No medications are available yet for this medical record group: ${activeDocumentGroup.document_type}.`
    : "Navigate to a grouped page to view its medications.";
};
